import { gql } from '@apollo/client'

export const EMERGENCY_CONTACT_FRAGMENT = gql`
	fragment emergencyContactFragment on MaestroWorkerEmergencyContact {
		externalId
		firstName
		id
		lastName
		phone
		relationship
	}
`

export const WORKER_ADDRESS_FRAGMENT = gql`
	fragment workerAddressFragment on MaestroWorkerAddress {
		id
		zipcode
		street
		city
		state
		country
		aptNumber
	}
`

// The recruiter candidate TABLES (SEARCH_WORKERS_QUERY) render a fixed set of columns;
// WORKER_FRAGMENT is shaped for the candidate PROFILE and pulls a good deal more. On the
// Matches tab that difference was measured at ~1.4s and ~90KB per page of 25 rows, most
// of it `resumes` — every resume's ActiveStorage signed URL is minted server-side, and
// no table column shows one.
//
// So the tables get their own fragment. Fields live here only if some consumer of
// SEARCH_WORKERS_QUERY reads them: the tab stores under recruiter-candidates/tabs and
// recruiter-home/tabs, the cells that receive the whole row (UserTitle -> id/avatar/
// email/firstName/lastName/fullName, CityCell/StateCell -> addresses[0].city/state/
// stateName), and the SPR-571 match-column helpers (jobSettings, accountTier, bilingual,
// workerDisciplineSpecialties).
//
// Adding a column? Add its field here, not to WORKER_FRAGMENT.
export const WORKER_LIST_FRAGMENT = gql`
	fragment workerListFragment on MaestroWorker {
		id
		email
		firstName
		lastName
		fullName
		phone
		status
		createdAt
		externalFriendlyId
		atsStatus
		atsSource
		engagementScore
		profilePercentageCompleted
		licenseStates
		preferredWorkTypes
		bilingual
		jobSettings
		accountTier
		matchingPercentage
		matchingExplanation
		matchingExplanationDetails {
			text
			level
		}
		noTalentMatches
		jobApplicantsCount
		activeSubmittedJobsCount
		submissionsTotalCount
		internalSub
		externalSub
		# Optional columns (see worker-optional-columns.js): off by default, offered
		# in Table settings. Scalars only, and deliberately so — the note above is
		# about resumes and their signed URLs, not about ten more primitives, and a
		# column nobody can switch on is worth nothing.
		availableStartDate
		compactLicense
		licenseStatus
		totalExperienceInMonths
		recentJobTitle
		region
		source
		zipcode
		currentWorkerAssignmentsCount
		submissionReadyCount
		# Feeds the tooltip behind the Engagement Score column (the GenericScore explanation prop).
		recentActivities {
			createdAt
			eventTime
			id
			key
			message
		}
		# CityCell / StateCell read addresses[0] only, and only these three values.
		addresses {
			id
			city
			state
			stateName
		}
		# lastSignInAt alone answers "has this candidate ever got in". The other three
		# are what the Candidate App Access screen needs to say WHY somebody never
		# did, and which of Send / Resend invite to offer — see
		# ~/features/recruiter-role/access-state. All four are scalars on a record
		# the list already joins, so nothing new is fetched per row.
		user {
			id
			username
			lastSignInAt
			welcomeEmailSentAt
			changePasswordRequired
			tempPasswordExpiredAt
		}
		avatar {
			id
			file {
				fileUrl
			}
		}
		workerDisciplineSpecialties {
			id
			discipline {
				disciplineName
				id
			}
			specialty {
				name
				id
				hasSkillChecklist
			}
			isPrimary
		}
		workerDisciplines {
			id
			discipline {
				disciplineName
				id
			}
		}
		employer {
			avatar {
				file {
					fileUrl
				}
			}
			email
			firstName
			fullName
			id
			lastName
			phone
			title
		}
		# Staffing Specialist — see WORKER_FRAGMENT.
		secondEmployer {
			avatar {
				file {
					fileUrl
				}
			}
			email
			firstName
			fullName
			id
			lastName
			phone
			title
		}
	}
`

// BE's one definition of outreach, on the row: the V2 Last contact column and the
// "not contacted" lists read the same indexed field, at no extra query cost.
//
// Kept OUT of WORKER_LIST_FRAGMENT on purpose. Both fields ship with Maestro V2 only, and
// a gem without V2 (GHR's ghr-master) rejects the WHOLE document at validation time —
// `@include(if: false)` does not help, a skipped field is still validated against the
// schema. Selecting them in the shared fragment blanked every Classic candidate table on
// GHR. Only SEARCH_WORKERS_WITH_OUTREACH_QUERY, sent from V2 screens, spreads this.
export const WORKER_OUTREACH_FRAGMENT = gql`
	fragment workerOutreachFragment on MaestroWorker {
		contacted
		lastContactedAt
	}
`

export const WORKER_FRAGMENT = gql`
	fragment workerFragment on MaestroWorker {
		id
		addresses {
			street
			city
			state
			stateName
			country
			default
			zipcode
			id
		}
		resumes {
			id
			file {
				fileUrl
				filename
				contentType
				blobId
			}
		}
		workerEmergencyContacts {
			firstName
			lastName
			phone
			relationship
		}
		externalFriendlyId
		phone
		engagementScore
		matchingPercentage
		matchingExplanation
		matchingExplanationDetails {
			text
			level
		}
		recentActivities {
			createdAt
			eventTime
			id
			key
			message
		}
		profilePercentageCompleted
		agreedToTerms
		availableStartDate
		compactLicense
		createdAt
		email
		externalCreatedAt
		firstName
		isActiveStatus
		lastName
		fullName
		phone
		phoneVerifiedAt
		status
		internalSub
		externalSub
		jobApplicantsCount
		atsStatus
		activeSubmittedJobsCount
		noTalentMatches
		atsSource
		licenseStates
		submissionsTotalCount
		# Same four timestamps the list fragment carries, plus signInCount: the App
		# Access tab on this profile answers why a candidate cannot sign in and which
		# of Send / Resend invite to offer — see ~/features/recruiter-role/access-state.
		user {
			id
			username
			lastSignInAt
			signInCount
			welcomeEmailSentAt
			changePasswordRequired
			tempPasswordExpiredAt
		}
		avatar {
			id
			file {
				fileUrl
			}
		}
		workAuthorizedAt
		zipcode
		tags {
			id
			name
		}
		workerDisciplineSpecialties {
			id
			discipline {
				disciplineName
				id
			}
			specialty {
				name
				id
				hasSkillChecklist
			}
			isPrimary
		}
		workerDisciplines {
			id
			discipline {
				disciplineName
				id
			}
		}
		preferredShifts
		workerAvailabilities {
			id
			fromDatetime
			status
			toDatetime
		}
		employer {
			avatar {
				file {
					fileUrl
				}
			}
			email
			firstName
			fullName
			id
			lastName
			phone
			title
		}
		# Staffing Specialist — rendered next to Assigned Recruiter behind the
		# maestro_show_staffing_specialist flag. Same shape as employer so both
		# columns can share the UserTitle cell renderer.
		secondEmployer {
			avatar {
				file {
					fileUrl
				}
			}
			email
			firstName
			fullName
			id
			lastName
			phone
			title
		}
		preferredWorkTypes
		enableEmployerAlert
		bilingual
		jobSettings
		accountTier
	}
`

export const LOCATION_FRAGMENT = gql`
	fragment locationFragment on MaestroLocation {
		id
		address
		addressState
		city
		country
		zipcode
	}
`

export const ACTIVE_JOB_FRAGMENT = gql`
	fragment jobFragment on MaestroJob {
		id
		title
		externalJobId
		startDate
		duration
		matchingPercentage
		matchingExplanation
		matchingExplanationDetails {
			text
			level
		}
		weeklyPayAmount
		minWeeklyPayAmount
		hourlyPayHigh
		hourlyPayLow
		stipends
		taxableHigh
		taxableLow
		specialty
		hospital
		discipline
		isFavorited
		status
		expiredDate
		atsStatus
		isPublish
		locationImage {
			thumbnailImage {
				imageUrls(size: ["100x100"])
			}
			webImage {
				fileUrl
			}
		}
		location {
			...locationFragment
		}
		jobApplicant {
			id
			status
			workerAssignment {
				endDate
			}
		}
		shifts
		hoursPerWeek
		shiftsPerWeek
		externalJobId
		licensesRequired
		licenseStateRequired
		certificationsRequired
	}
	${LOCATION_FRAGMENT}
`

export const JOB_FRAGMENT = gql`
	fragment jobFragment on MaestroJob {
		location {
			region
			city
			zipcode
			addressState
		}
		locationImage {
			thumbnailImage {
				imageUrls(size: ["100x100"])
			}
			webImage {
				fileUrl
			}
		}
		customStartDate
		applicantCount
		createdAt
		discipline
		duration
		facilityType
		hospital
		frequency
		id
		jobType
		externalCreatedAt
		matchingPercentage
		matchingExplanation
		matchingExplanationDetails {
			text
			level
		}
		minWeeklyPayAmount
		shifts
		skillLongName
		specialty
		startDate
		status
		title
		updatedAt
		isAsap
		viewCount
		weeklyPayAmount
		slotsNumber
		atsStatus
		isPublish
	}
`

export const JOB_DETAIL_GENERAL_FRAGMENT = gql`
	fragment jobDetailGeneralFragment on MaestroJobGeneral {
		disciplines {
			disciplineName
			id
			disciplineType
		}
		jobGeneralSpecialties {
			id
			specialtyId
			disciplineId
			perDiemPayRate
			perDiemPayRateType
		}
		address {
			id
			country
			state
			stateName
			city
			latitude
			zipcode
			longitude
			locationImage {
				webImage {
					fileUrl
				}
			}
		}
		benefitsText
		certificationsRequired
		description
		maestroDescription
		externalId
		facility {
			id
			name
		}
		hotJob
		id
		isPerDiem
		licenseStateCompact
		licenseStateRequired
		payCurrency
		settings
		title
		workTypes
		specialties {
			externalId
			id
			jobsCount
			longName
			name
		}
	}
`

export const RECRUITER_JOB_BASE_FRAGMENT = gql`
	fragment recruiterJobBaseFragment on MaestroJobBase {
		benefitsText
		billRate
		customFields
		externalFriendlyId
		isPerDiemLongTerm
		duration
		durationType
		endDatetime
		externalId
		hoursPerDay
		hoursPerWeek
		timezone
		id
		isFavorited
		favoritedAt
		atsSource
		metadata
		jobGeneral {
			...jobDetailGeneralFragment
		}
		matchingPercentage
		matchingExplanation
		matchingExplanationDetails {
			text
			level
		}
		payAmountMax
		payAmountMin
		publishedAt
		payRate
		payType
		requirementsText
		shifts
		slotsNumber
		startDatetime
		viewedCount
		status
		jobApplicant {
			status
			appliedDate
			submittedDatetime
		}
		workerAssignment {
			id
			status
			payRate
			payType
			salary
			scheduledStartTime
		}
	}
	${JOB_DETAIL_GENERAL_FRAGMENT}
`

export const RECRUITER_JOB_FRAGMENT = gql`
	fragment recruiterJobFragment on MaestroJob {
		duration
		durationType
		endDatetime
		hoursPerDay
		hoursPerWeek
		timezone
		id
		isFavorited
		favoritedAt
		atsSource
		jobGeneral {
			...jobDetailGeneralFragment
		}
		externalFriendlyId
		duration
		matchingPercentage
		matchingExplanation
		matchingExplanationDetails {
			text
			level
		}
		payAmountMax
		payAmountMin
		publishedAt
		payRate
		payType
		requirementsText
		shifts
		slotsNumber
		startDatetime
		viewedCount
		status
		atsStatus
		isPublish
		jobApplicant {
			status
			appliedDate
			submittedDatetime
		}
		workerAssignment {
			id
			status
			payRate
			payType
			salary
			scheduledStartTime
		}
	}
	${JOB_DETAIL_GENERAL_FRAGMENT}
`

export const COMPANY_FRAGMENT = gql`
	fragment companyFragment on MaestroCompany {
		address
		addressState
		city
		country
		description
		id
		mainContactEmail
		mainContactName
		companyConfig {
			enableHourlySearch
		}
		name
		phone
		zip
	}
`

export const WORKER_CREDENTIAL_FRAGMENT = gql`
	fragment workerCredentialFragment on MaestroWorkerCredential {
		company {
			...companyFragment
		}
		createdAt
		expirationDate
		id
		name
		status
		worker {
			...workerFragment
		}
	}
	${WORKER_FRAGMENT}
	${COMPANY_FRAGMENT}
`

export const WORKING_AUTHORIZATION_FRAGMENT = gql`
	fragment workingAuthorizationFragment on MaestroWorkingAuthorization {
		id
		dateOfBirth
		externalId
		legalRightToWork
		npi
		ssn
		ssnMasked
	}
`

export const WORK_EXPERIENCE_FRAGMENT = gql`
	fragment workExperienceFragment on MaestroWorkExperience {
		id
		agency
		city
		country
		currentlyEmployed
		endDate
		externalId
		facilityName
		notes
		reasonForLeaving
		state
		startDate
		stateName
		title
		unitType
		unitDescription
		traumaLevel
		teachingFacility
		numberOfBeds
		description
	}
`

export const SALESFORCE_INTEGRATION_FRAGMENT = gql`
	fragment salesforceIntegrationFragment on SalesforceIntegration {
		id
		companyId
		businessDay
		weekending
	}
`

export const ONBOARDING_PART_FRAGMENT = gql`
	fragment onboardingPartFragment on OnboardingPart {
		id
		name
		description
	}
`
export const DIRECT_UPLOAD_FRAGMENT = gql`
	fragment directUploadFragment on MaestroDirectUpload {
		blobId
		headers
		preSignedUrl
		signedBlobId
		url
	}
`

export const WORKER_CERTIFICATION_FRAGMENT = gql`
	fragment workerCertificationFragment on MaestroWorkerCertification {
		certificationFiles {
			documentType
			id
			note
			file {
				fileUrl
			}
		}
		certificationName
		doesNotExpire
		effectiveDate
		externalId
		id
		number
	}
`

export const WORKER_REFERENCE_FRAGMENT = gql`
	fragment workerReferenceFragment on MaestroWorkerReference {
		id
		city
		country
		email
		externalId
		facilityName
		firstName
		fromDate
		jobTitle
		lastName
		phone
		referenceType
		relationship
		state
		stateName
		toDate
		waitToContact
		zipcode
	}
`
export const IMAGE_FILE_FRAGMENT = gql`
	fragment imageFileFragment on File {
		id
		fileUrl
		contentType
	}
`

export const FILE_FRAGMENT = gql`
	fragment fileFragment on File {
		id
		filename
		fileUrl
		contentType
	}
`

export const WORKER_EDUCATION_FRAGMENT = gql`
	fragment workerEducationFragment on MaestroWorkerEducation {
		id
		schoolName
		state
		city
		country
		degreeName
		graduationDate
		comments
	}
`

export const WORKER_DISCIPLINE_SPECIALTY_FRAGMENT = gql`
	fragment workerDisciplineSpecialtyFragment on MaestroWorkerDisciplineSpecialty {
		completedAllScl
		discipline {
			id
			disciplineName
			specialties {
				id
				name
			}
		}
		hasSkillChecklist
		id
		isPrimary
		specialty {
			id
			name
		}
	}
`

export const WORKER_CETIFICATION_OVERVIEW_FRAGMENT = gql`
	fragment workerCertificationOverviewFragment on WorkerCertificationOverview {
		id
		workerCertifications {
			...workerCertificationFragment
		}
	}

	${WORKER_CERTIFICATION_FRAGMENT}
`

export const WORKER_REFERENCE_OVERVIEW_FRAGMENT = gql`
	fragment workerReferenceOverviewFragment on WorkerReferenceOverview {
		id
		workerReferences {
			...workerReferenceFragment
		}
	}

	${WORKER_REFERENCE_FRAGMENT}
`

export const WORKER_EDUCATION_OVERVIEW_FRAGMENT = gql`
	fragment workerEducationOverviewFragment on WorkerEducationOverview {
		id
		workerEducations {
			...workerEducationFragment
		}
	}

	${WORKER_EDUCATION_FRAGMENT}
`

export const WORK_EXPERIENCE_OVERVIEW_FRAGMENT = gql`
	fragment workExperienceOverview on MaestroWorkExperience {
		id
		agency
		city
		country
		currentlyEmployed
		endDate
		externalId
		facilityName
		notes
		reasonForLeaving
		state
		startDate
		stateName
		title
		travelAssignment
		unitType
		description
	}
`

export const WORKER_ONBOARDING_PART_INTEGRATION_FRAGMENT = gql`
	fragment workerOnboardingPartIntegrationFragment on WorkerOnboardingPartIntegration {
		... on UserTermsOfService {
			id
			agreedAt
			termsOfService {
				id
				url
				version
			}
		}
		... on ResumeOnboarding {
			id
			worker {
				resumes {
					blobId
					id
					filename
					fileUrl
					contentType
					thumbnails
					imageUrls(size: ["100x100", "200x200", "500x500"])
				}
			}
		}
		... on SpecialtyOnboarding {
			id
			workerSpecialties {
				category
				id
				isPrimary
				completedChecklist
				specialty
				specialtyLongName
				workerSkillChecklistSections {
					id
					name
					workerSkillChecklists {
						frequency
						proficiency
						question
						id
					}
				}
			}
		}
		... on WorkingPreferredLocationOnboarding {
			id
			worker {
				id
				availableStartDate
			}
			workingPreferredLocations {
				id
				preferredWorkingState
				preferredWorkingCity
			}
		}

		... on WorkerReferenceOnboarding {
			id
			workerReferences {
				id
				facilityName
				contactEmail
				contactFullName
				contactFirstName
				contactLastName
				contactPhone
				jobTitle
				referenceType
				relationship
				salutation
				state
				city
				workedFrom
				workedTo
			}
		}
	}
`

export const WORKER_ONBOARDING_PART_FRAGMENT = gql`
	fragment workerOnboardingPartFragment on WorkerOnboardingPart {
		id
		integrationConfigurations
		integrationObject
		state
		onboardingPart {
			id
			name
		}
		integration {
			...workerOnboardingPartIntegrationFragment
		}
	}

	${WORKER_ONBOARDING_PART_INTEGRATION_FRAGMENT}
`

export const REFERRED_TRAVELER_FRAGMENT = gql`
	fragment referredTravelerFragment on ReferredTraveler {
		id
		firstName
		lastName
		email
		phone
		relationship
		discipline
	}
`

export const RECRUITER_FRAGMENT = gql`
	fragment recruiterFragment on Recruiter {
		companyId
		email
		id
		name
		phone
		title
		changePasswordRequired
	}
`

export const EMPLOYER_FRAGMENT = gql`
	fragment employerFragment on Employer {
		email
		id
		firstName
		lastName
		phone
		user {
			changePasswordRequired
			id
			tempPasswordExpiredAt
		}
		roles {
			id
			name
			scope
		}
	}
`

export const MAESTRO_EMPLOYER_FRAGMENT = gql`
	fragment maestroEmployerFragment on MaestroEmployer {
		email
		id
		firstName
		lastName
		fullName
		phone
		user {
			changePasswordRequired
			id
			tempPasswordExpiredAt
			welcomeEmailSentAt
		}
		roles {
			id
			name
			scope
		}
	}
`

export const JOB_APPLICANT_FRAGMENT = gql`
	fragment jobApplicantFragment on MaestroJobApplicant {
		createdAt
		id
		status
		updatedAt
		stage
		stageChangedAt
		translatedStatus
		job {
			...jobFragment
		}
		worker {
			...workerFragment
		}
	}
	${WORKER_FRAGMENT}
	${JOB_FRAGMENT}
`

export const WORKED_SHIFT_FRAGMENT = gql`
	fragment workedShiftFragment on WorkedShift {
		id
		workDate
		taskExternalId

		startTime
		endTime

		firstBreakStartTime
		firstBreakEndTime

		secondBreakStartTime
		secondBreakEndTime
	}
`

export const WORKED_EXPENSE_FRAGMENT = gql`
	fragment workedExpenseFragment on WorkerExpense {
		id
		category
		amount
		notes
		receiptImages {
			id
			filename
			fileUrl
		}
	}
`

export const WORKED_TIMESHEET_FRAGMENT = gql`
	fragment workedTimesheetFragment on WorkedTimesheet {
		id
		notes
		startDate
		endDate
		preEmploymentHours
		status
		workedShiftsOverview
	}
`

export const RECRUITER_WORKER_ONBOARDING_PART_INTEGRATION_FRAGMENT = gql`
	fragment workerOnboardingPartIntegrationFragment on WorkerOnboardingPartIntegration {
		... on WorkerCertificationOverview {
			...workerCertificationOverviewFragment
		}

		... on WorkExperienceOverview {
			...workExperienceOverview
		}

		... on WorkerReferenceOverview {
			...workerReferenceOverviewFragment
		}

		... on WorkerEducationOverview {
			...workerEducationOverviewFragment
		}

		... on UserTermsOfService {
			id
			agreedAt
			termsOfService {
				id
				url
				version
			}
		}

		... on CombinationOnboardingPart {
			id
			workerAddress {
				...workerAddressFragment
			}

			workingPreferredLocations {
				id
				preferredWorkingState
			}
		}
	}

	${WORKER_CETIFICATION_OVERVIEW_FRAGMENT}
	${WORK_EXPERIENCE_OVERVIEW_FRAGMENT}
	${WORKER_REFERENCE_OVERVIEW_FRAGMENT}
	${WORKER_EDUCATION_OVERVIEW_FRAGMENT}
	${WORKER_ADDRESS_FRAGMENT}
`

export const RECRUITER_WORKER_ONBOARDING_PART_FRAGMENT = gql`
	fragment workerOnboardingPartFragment on WorkerOnboardingPart {
		id
		integrationConfigurations
		integrationObject
		state
		integration {
			...workerOnboardingPartIntegrationFragment
		}
	}

	${RECRUITER_WORKER_ONBOARDING_PART_INTEGRATION_FRAGMENT}
`

export const WORKER_ASSIGNMENT_FRAGMENT = gql`
	fragment workerAssignmentFragment on WorkerAssignment {
		createdAt
		endDate
		facilitySigned
		id
		isCurrentAssignment
		isSpecialWorkWeek
		job {
			...jobFragment
		}
		worker {
			...workerFragment
		}
		shiftTypes
		startDate
		status
		translatedStatus
		updatedAt
	}
	${JOB_FRAGMENT}
	${WORKER_FRAGMENT}
`

export const MAESTRO_FAVORITABLE_UNION_FRAGMENT = gql`
	fragment maestroFavoritableUnionFragment on MaestroFavoritableUnion {
		__typename
		... on MaestroWorker {
			id
			firstName
			lastName
			fullName
			externalFriendlyId
		}
		... on MaestroJob {
			id
			externalFriendlyId
			matchFeedbackAction
			matchingExplanation
			matchingExplanationDetails {
				text
				level
			}
			matchingPercentage
			jobGeneral {
				hotJob
				settings
				title
				workTypes
			}
			facility {
				name
			}
			startDatetime
			endDatetime
		}
	}
`
